import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../../lib/api.js';
import { Table, type Column } from '../../components/ui/Table.js';
import { Dialog } from '../../components/ui/Dialog.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { StatusPill, type PillVariant } from '../../components/ui/StatusPill.js';
import { useToast } from '../../components/ui/Toast.js';
import {
  ProviderNotConfiguredCard,
  errMessage,
  isProviderNotConfigured,
  retryUnlessClientError,
  splitCsv,
} from './shared.js';
import './infrastructure.css';

interface Droplet {
  id: number;
  internalId: string | null;
  name: string;
  status: string;
  regionSlug: string | null;
  sizeSlug: string | null;
  publicIp: string | null;
  tags: string[];
  createdAt: string | null;
}

interface DoRegion {
  slug: string;
  name: string;
  available: boolean;
  sizes: string[];
}

interface DoSize {
  slug: string;
  memory: number;
  vcpus: number;
  disk: number;
  priceMonthly: number;
  available: boolean;
  regions: string[];
}

interface DoImage {
  id: number;
  slug: string | null;
  name: string;
  distribution: string | null;
  type: string | null;
}

interface DoSshKey {
  id: number;
  fingerprint: string | null;
  name: string;
}

const DROPLET_ACTIONS = ['power_on', 'power_off', 'reboot', 'shutdown'] as const;
type DropletAction = (typeof DROPLET_ACTIONS)[number];

const ACTION_LABELS: Record<DropletAction, string> = {
  power_on: 'Power on',
  power_off: 'Power off',
  reboot: 'Reboot',
  shutdown: 'Shutdown',
};

function PrepDialog({ dropletId, dropletName, onClose }: { dropletId: string; dropletName: string; onClose: () => void }) {
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [runId, setRunId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const components = useQuery<Array<{ key: string; name: string; description: string }>>({
    queryKey: ['prep-components'],
    queryFn: () => apiFetch('/droplets/prep/components'),
  });

  const run = useQuery<{ status: string; log: string; results: Record<string, string> } | null>({
    queryKey: ['prep-run', dropletId, runId],
    queryFn: () => apiFetch(`/droplets/${dropletId}/prep/runs/${runId}`),
    enabled: runId !== null,
    refetchInterval: (q) => (q.state.data && q.state.data.status === 'running' ? 1500 : false),
  });

  const start = useMutation({
    mutationFn: (components: 'all' | string[]) =>
      apiFetch<{ runId: string }>(`/droplets/${dropletId}/prep`, { method: 'POST', body: { components } }),
    onSuccess: (r) => { setError(''); setRunId(r.runId); },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'failed to start prep'),
  });

  const busy = start.isPending || run.data?.status === 'running';

  return (
    <div className="dialog-backdrop" onClick={busy ? undefined : onClose}>
      <div className="card dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <h2>Server prep — <span className="mono">{dropletName}</span></h2>
        {error ? <p className="dim">{error}</p> : null}
        {!runId ? (
          <>
            <p className="dim">Select components to install/update (re-running is safe), or install everything.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(components.data ?? []).map((c) => (
                <label key={c.key} className="check-row" style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={checked.has(c.key)}
                    onChange={(e) => setChecked((prev) => { const n = new Set(prev); e.target.checked ? n.add(c.key) : n.delete(c.key); return n; })}
                  />
                  <span>{c.name} <span className="dim">— {c.description}</span></span>
                </label>
              ))}
            </div>
            <div className="dialog-actions">
              <button className="btn" onClick={onClose}>Cancel</button>
              <button className="btn" disabled={checked.size === 0 || start.isPending} onClick={() => start.mutate([...checked])}>
                Install selected
              </button>
              <button className="btn btn-amber" disabled={start.isPending} onClick={() => start.mutate('all')}>
                Install / Update all
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="mono">status: {run.data?.status ?? 'starting…'}</p>
            <pre className="mono" style={{ maxHeight: 360, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 12 }}>
              {run.data?.log ?? ''}
            </pre>
            <div className="dialog-actions">
              <button className="btn btn-amber" disabled={busy} onClick={onClose}>
                {busy ? 'Running…' : 'Close'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function statusVariant(status: string): PillVariant {
  if (status === 'active') return 'ok';
  if (status === 'new') return 'warn';
  if (status === 'off' || status === 'archive') return 'err';
  return 'neutral';
}

export function DropletsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const dropletsQuery = useQuery<Droplet[]>({
    queryKey: ['do', 'droplets'],
    queryFn: () => apiFetch<Droplet[]>('/do/droplets'),
    retry: retryUnlessClientError,
  });

  // ── create dialog ──
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [region, setRegion] = useState('');
  const [size, setSize] = useState('');
  const [image, setImage] = useState('');
  const [sshKeyIds, setSshKeyIds] = useState<number[]>([]);
  const [tagsInput, setTagsInput] = useState('');

  const resetForm = () => {
    setName('');
    setRegion('');
    setSize('');
    setImage('');
    setSshKeyIds([]);
    setTagsInput('');
  };

  const closeCreate = () => {
    setCreateOpen(false);
    resetForm();
  };

  const regionsQuery = useQuery<DoRegion[]>({
    queryKey: ['do', 'regions'],
    queryFn: () => apiFetch<DoRegion[]>('/do/regions'),
    enabled: createOpen,
    retry: retryUnlessClientError,
  });
  const sizesQuery = useQuery<DoSize[]>({
    queryKey: ['do', 'sizes'],
    queryFn: () => apiFetch<DoSize[]>('/do/sizes'),
    enabled: createOpen,
    retry: retryUnlessClientError,
  });
  const imagesQuery = useQuery<DoImage[]>({
    queryKey: ['do', 'images', 'distribution'],
    queryFn: () => apiFetch<DoImage[]>('/do/images?type=distribution'),
    enabled: createOpen,
    retry: retryUnlessClientError,
  });
  const sshKeysQuery = useQuery<DoSshKey[]>({
    queryKey: ['do', 'ssh-keys'],
    queryFn: () => apiFetch<DoSshKey[]>('/do/ssh-keys'),
    enabled: createOpen,
    retry: retryUnlessClientError,
  });

  const selectedRegion = regionsQuery.data?.find((r) => r.slug === region);
  const sizesForRegion = (sizesQuery.data ?? []).filter(
    (s) => !selectedRegion || s.regions.includes(selectedRegion.slug),
  );

  const createMut = useMutation({
    mutationFn: (body: {
      name: string;
      region: string;
      size: string;
      image: string;
      tags?: string[];
      ssh_keys?: number[];
    }) => apiFetch<Droplet>('/do/droplets', { method: 'POST', body }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['do', 'droplets'] });
      closeCreate();
      toast.success(`Droplet "${created.name}" is being created`);
    },
    onError: (err) => toast.error(errMessage(err, 'failed to create droplet')),
  });

  const submitCreate = () => {
    const tags = splitCsv(tagsInput);
    createMut.mutate({
      name: name.trim(),
      region,
      size,
      image,
      ...(tags.length > 0 ? { tags } : {}),
      ...(sshKeyIds.length > 0 ? { ssh_keys: sshKeyIds } : {}),
    });
  };

  // ── power actions ──
  const actionMut = useMutation({
    mutationFn: ({ id, action }: { id: number; action: DropletAction; name: string }) =>
      apiFetch<{ actionId: number | null; status: string | null }>(`/do/droplets/${id}/actions`, {
        method: 'POST',
        body: { action },
      }),
    onSuccess: async (_res, vars) => {
      await queryClient.invalidateQueries({ queryKey: ['do', 'droplets'] });
      toast.success(`${ACTION_LABELS[vars.action]} requested for "${vars.name}"`);
    },
    onError: (err) => toast.error(errMessage(err, 'droplet action failed')),
  });

  // ── prep ──
  const [prepping, setPrepping] = useState<{ id: string; name: string } | null>(null);

  // ── delete ──
  const [deleteTarget, setDeleteTarget] = useState<Droplet | null>(null);
  const deleteMut = useMutation({
    mutationFn: (d: Droplet) =>
      apiFetch<{ ok: boolean }>(`/do/droplets/${d.id}`, {
        method: 'DELETE',
        body: { confirmName: d.name },
      }),
    onSuccess: async (_res, d) => {
      await queryClient.invalidateQueries({ queryKey: ['do', 'droplets'] });
      toast.success(`Droplet "${d.name}" deleted`);
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(errMessage(err, 'delete failed')),
  });

  const toggleSshKey = (id: number) => {
    setSshKeyIds((cur) => (cur.includes(id) ? cur.filter((k) => k !== id) : [...cur, id]));
  };

  const rowActions = (d: Droplet): DropletAction[] =>
    d.status === 'off' ? ['power_on'] : ['reboot', 'shutdown', 'power_off'];

  const columns: Column<Droplet>[] = [
    { key: 'name', header: 'Name', render: (d) => <span className="infra-name">{d.name}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (d) => <StatusPill variant={statusVariant(d.status)}>{d.status}</StatusPill>,
    },
    {
      key: 'region',
      header: 'Region',
      render: (d) => <span className="ui-cell-mute">{d.regionSlug ?? '—'}</span>,
    },
    {
      key: 'size',
      header: 'Size',
      render: (d) => <span className="mono ui-cell-mute">{d.sizeSlug ?? '—'}</span>,
    },
    {
      key: 'ip',
      header: 'Public IP',
      render: (d) => <span className="mono">{d.publicIp ?? '—'}</span>,
    },
    {
      key: 'tags',
      header: 'Tags',
      render: (d) =>
        d.tags.length === 0 ? (
          <span className="ui-cell-mute">—</span>
        ) : (
          <div className="tag-chips">
            {d.tags.map((t) => (
              <span key={t} className="tag-chip">
                {t}
              </span>
            ))}
          </div>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (d) => (
        <div className="ui-cell-actions">
          {rowActions(d).map((action) => (
            <button
              key={action}
              className="btn btn-ghost btn-sm"
              disabled={actionMut.isPending && actionMut.variables?.id === d.id}
              onClick={() => actionMut.mutate({ id: d.id, action, name: d.name })}
            >
              {ACTION_LABELS[action]}
            </button>
          ))}
          {d.internalId ? (
            <button className="btn btn-ghost btn-sm" onClick={() => setPrepping({ id: d.internalId!, name: d.name })}>
              Prep
            </button>
          ) : null}
          <button className="btn btn-ghost-danger btn-sm" onClick={() => setDeleteTarget(d)}>
            Delete
          </button>
        </div>
      ),
    },
  ];

  const canCreate = name.trim().length > 0 && region !== '' && size !== '' && image !== '';

  if (isProviderNotConfigured(dropletsQuery.error)) {
    return (
      <div>
        <div className="page-head">
          <div>
            <h1>Droplets</h1>
            <p className="page-sub">DigitalOcean virtual machines.</p>
          </div>
        </div>
        <ProviderNotConfiguredCard provider="DigitalOcean" message={dropletsQuery.error.message} />
      </div>
    );
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Droplets</h1>
          <p className="page-sub">DigitalOcean virtual machines.</p>
        </div>
        <button className="btn btn-amber" onClick={() => setCreateOpen(true)}>
          Create droplet
        </button>
      </div>

      <Table
        columns={columns}
        rows={dropletsQuery.data ?? []}
        rowKey={(d) => String(d.id)}
        empty={
          dropletsQuery.isLoading
            ? 'Loading…'
            : dropletsQuery.isError
              ? errMessage(dropletsQuery.error, 'failed to load droplets')
              : 'No droplets yet. Create one to get started.'
        }
      />

      {/* create dialog */}
      <Dialog open={createOpen} onClose={closeCreate} title="Create droplet" width={520}>
        <form
          className="dialog-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (canCreate && !createMut.isPending) submitCreate();
          }}
        >
          <div className="field">
            <label htmlFor="droplet-name">Name</label>
            <input
              id="droplet-name"
              autoFocus
              maxLength={255}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="web-01"
            />
          </div>
          <div className="field-grid-2">
            <div className="field">
              <label htmlFor="droplet-region">Region</label>
              <select
                id="droplet-region"
                value={region}
                onChange={(e) => {
                  setRegion(e.target.value);
                  setSize('');
                }}
              >
                <option value="" disabled>
                  {regionsQuery.isLoading ? 'Loading…' : 'Select region'}
                </option>
                {(regionsQuery.data ?? [])
                  .filter((r) => r.available)
                  .map((r) => (
                    <option key={r.slug} value={r.slug}>
                      {r.name} ({r.slug})
                    </option>
                  ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="droplet-size">Size</label>
              <select id="droplet-size" value={size} onChange={(e) => setSize(e.target.value)}>
                <option value="" disabled>
                  {sizesQuery.isLoading ? 'Loading…' : 'Select size'}
                </option>
                {sizesForRegion
                  .filter((s) => s.available)
                  .map((s) => (
                    <option key={s.slug} value={s.slug}>
                      {s.slug}
                      {s.priceMonthly > 0 ? ` — $${s.priceMonthly}/mo` : ''}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <div className="field">
            <label htmlFor="droplet-image">Image</label>
            <select id="droplet-image" value={image} onChange={(e) => setImage(e.target.value)}>
              <option value="" disabled>
                {imagesQuery.isLoading ? 'Loading…' : 'Select image'}
              </option>
              {(imagesQuery.data ?? []).map((img) => (
                <option key={img.id} value={img.slug ?? String(img.id)}>
                  {img.distribution ? `${img.distribution} — ` : ''}
                  {img.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>SSH keys</label>
            <div className="check-list">
              {sshKeysQuery.isLoading ? (
                <span className="check-list-empty">Loading…</span>
              ) : (sshKeysQuery.data ?? []).length === 0 ? (
                <span className="check-list-empty">No SSH keys on the DigitalOcean account.</span>
              ) : (
                (sshKeysQuery.data ?? []).map((k) => (
                  <label key={k.id} className="check-list-item">
                    <input
                      type="checkbox"
                      checked={sshKeyIds.includes(k.id)}
                      onChange={() => toggleSshKey(k.id)}
                    />
                    {k.name}
                    {k.fingerprint ? (
                      <span className="mono ui-cell-mute">{k.fingerprint.slice(0, 23)}…</span>
                    ) : null}
                  </label>
                ))
              )}
            </div>
          </div>
          <div className="field">
            <label htmlFor="droplet-tags">Tags (comma separated)</label>
            <input
              id="droplet-tags"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="web, production"
            />
          </div>
          <div className="dialog-actions">
            <button type="button" className="btn btn-ghost" onClick={closeCreate}>
              Cancel
            </button>
            <button type="submit" className="btn btn-amber" disabled={!canCreate || createMut.isPending}>
              {createMut.isPending ? 'Creating…' : 'Create droplet'}
            </button>
          </div>
        </form>
      </Dialog>

      {/* delete confirm */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget)}
        title="Destroy droplet"
        targetName={deleteTarget?.name ?? ''}
        description="The droplet and its local disk are destroyed immediately."
        confirmLabel="Destroy droplet"
        busy={deleteMut.isPending}
      />

      {/* prep dialog */}
      {prepping ? (
        <PrepDialog
          dropletId={prepping.id}
          dropletName={prepping.name}
          onClose={() => setPrepping(null)}
        />
      ) : null}
    </div>
  );
}
